import Keycloak from 'keycloak-js';
import baseapi, { API_BASE } from '../baseapi';
import { clearAuthSession } from '../pages/LoginPage';

/**
 * Sign-in mode is decided by the server this UI is served from, so one build works everywhere:
 *   GET /api/auth/config -> { keycloak: { url, realm, client_id } }  -> Keycloak login (main AI server)
 *   endpoint missing (404) or { keycloak: null }                    -> built-in login page (mini, Spark)
 * A network error or 5xx is NOT treated as "no Keycloak": the app waits and retries, so a backend
 * hiccup on a Keycloak-protected site never falls back to the built-in login.
 */

export interface KeycloakSettings {
  url: string;       // absolute, or relative to this site (e.g. "/auth")
  realm: string;
  clientId: string;
}

export type AuthMode = 'keycloak' | 'local';

let keycloak: Keycloak | null = null;
let bootstrapPromise: Promise<AuthMode> | null = null;

async function loadKeycloakSettings(): Promise<KeycloakSettings | null> {
  const res = await fetch(`${API_BASE}auth/config`, { headers: { Accept: 'application/json' }, cache: 'no-store' });
  if (res.status === 404) return null;                       // backend without the endpoint
  if (!res.ok) throw new Error(`auth config: HTTP ${res.status}`);
  if (!(res.headers.get('content-type') || '').includes('json')) return null;   // SPA fallback page
  const kc = (await res.json())?.keycloak;
  if (!kc) return null;
  const clientId = kc.client_id ?? kc.clientId;
  if (!kc.realm || !clientId) throw new Error('auth config: Keycloak realm / client id missing');
  return { url: new URL(kc.url || '/auth', window.location.origin).toString().replace(/\/$/, ''), realm: kc.realm, clientId };
}

async function startKeycloak(settings: KeycloakSettings): Promise<void> {
  const kc = new Keycloak({ url: settings.url, realm: settings.realm, clientId: settings.clientId });
  // login-required: an unauthenticated visitor is redirected to the Keycloak login page
  const authenticated = await kc.init({ onLoad: 'login-required', pkceMethod: 'S256', checkLoginIframe: false });
  if (!authenticated) {
    await kc.login();
    return;
  }
  kc.onTokenExpired = () => { kc.updateToken(30).catch(() => kc.login()); };
  baseapi.interceptors.request.use(async (config) => {
    try { await kc.updateToken(30); } catch { await kc.login(); }
    if (kc.token) config.headers.Authorization = `Bearer ${kc.token}`;
    return config;
  });
  keycloak = kc;
}

export function isPublicPath(): boolean {
  if (typeof window === 'undefined') return false;
  const p = window.location.pathname.toLowerCase();
  return (
    p === '/direction' ||
    p === '/direction/' ||
    p === '/directions' ||
    p === '/direction.html' ||
    p.includes('darshan_hoarding_display')
  );
}

/** Resolve the sign-in mode once per page load (safe under React StrictMode double effects). */
export function bootstrapAuth(): Promise<AuthMode> {
  // Public display screens (e.g. /direction hoarding) never require sign-in
  if (isPublicPath()) {
    return Promise.resolve('local' as const);
  }
  if (!bootstrapPromise) {
    bootstrapPromise = (async () => {
      const settings = await loadKeycloakSettings();
      if (!settings) return 'local' as const;
      await startKeycloak(settings);
      return 'keycloak' as const;
    })();
    bootstrapPromise.catch(() => { bootstrapPromise = null; });   // allow a retry after a failure
  }
  return bootstrapPromise;
}

/** Sign out of whichever login is in use. */
export function logout(): void {
  if (keycloak) {
    keycloak.logout({ redirectUri: `${window.location.origin}/` });
  } else {
    clearAuthSession();
  }
}

/** Signed-in user's name when Keycloak is in use. */
export function currentUser(): string | null {
  const t = keycloak?.tokenParsed as Record<string, unknown> | undefined;
  return (t?.preferred_username as string) || (t?.name as string) || null;
}
